import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { SpendModelPoint, SpendSessionPoint, SpendSummary, SpendToolPoint, TokenTypeCostPoint, Tool } from '@ace-tracker/shared';
import { CHART_OTHER_COLOR, CHART_SERIES_COLORS, chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { SPEND_METRIC_INFO } from '../metricInfo';
import { TOOL_COLORS } from '../toolColors';
import { ChartCard } from './ChartCard';
import { KpiCard } from './KpiCard';
import { EffectiveRateTable } from './EffectiveRateTable';
import { BudgetSection } from './BudgetSection';
import { TierMixSection } from './TierMixSection';
import { PricingBasisNote } from './PricingBasisNote';
import { TOOL_LABELS } from './Sidebar';
import { TableCard } from './TableCard';
import { WhatIfSimulator } from './WhatIfSimulator';

type FormatCurrency = (value: number, currency: string) => string;
type DailyMode = 'tool' | 'model';

const TOP_MODELS = 5;
const OTHER = 'Other models';
const TOKEN_TYPES: { key: keyof TokenTypeCostPoint; label: string; color: string }[] = [
  { key: 'input', label: 'Input', color: '#0058AB' },
  { key: 'output', label: 'Output', color: '#049670' },
  { key: 'cacheRead', label: 'Cache read', color: '#00B4D8' },
  { key: 'cacheWrite', label: 'Cache write', color: '#7B2FBE' },
];
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Spend view: indicative Claude Code and Codex cost by day, tool, model,
 * token type, project, and session. Only cost is compared across tools —
 * never raw tokens (see dashboard/AGENTS.md "API rules"). Copilot is shown
 * as "not included", never as zero.
 */
export function SpendDashboardBody({
  summary,
  formatCurrency,
  SectionHeading,
}: {
  summary: SpendSummary;
  formatCurrency: FormatCurrency;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  const money = (value: number) => formatCurrency(value, summary.currency);
  const typeTotals = summary.byModel.reduce(
    (sum, m) => ({
      input: sum.input + m.byType.input,
      output: sum.output + m.byType.output,
      cacheRead: sum.cacheRead + m.byType.cacheRead,
      cacheWrite: sum.cacheWrite + m.byType.cacheWrite,
    }),
    { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 },
  );
  const cacheShare = summary.totalCost > 0 ? (typeTotals.cacheRead + typeTotals.cacheWrite) / summary.totalCost : null;

  return (
    <>
      <section>
        <SectionHeading title="Spend at a glance" hint="Indicative cost, not an invoice" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <KpiCard
            hero
            label="Indicative spend"
            value={money(summary.totalCost)}
            meta={changeText(summary.totalCost, summary.previousTotalCost, money) ?? summary.comparisonUnavailableReason ?? undefined}
            info={SPEND_METRIC_INFO.totalSpend}
          />
          {[...summary.byTool].sort((a, b) => Number(a.cost === null) - Number(b.cost === null)).map((point) => (
            <ToolSpendCard key={point.tool} point={point} money={money} />
          ))}
        </div>
      </section>

      <BudgetSection money={money} SectionHeading={SectionHeading} />

      <section>
        <SectionHeading title="Daily spend" />
        <DailySpendChart summary={summary} money={money} />
      </section>

      <section>
        <SectionHeading
          title="Cost by token type"
          hint={cacheShare === null ? undefined : `Cache reads and writes are ${formatPercent(cacheShare)} of spend`}
          info={SPEND_METRIC_INFO.cacheShare}
        />
        <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {TOKEN_TYPES.map((type) => (
            <TokenTypeTotal key={type.key} label={type.label} color={type.color} value={typeTotals[type.key]} total={summary.totalCost} money={money} />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <TokenTypeChart byModel={summary.byModel} money={money} />
          <TokenTypeTable byModel={summary.byModel} money={money} />
        </div>
      </section>

      <section>
        <SectionHeading title="Effective price per million tokens" hint="What usage really cost per token after caching" info={SPEND_METRIC_INFO.effectiveRate} />
        <EffectiveRateTable byModel={summary.byModel} money={money} />
      </section>

      <TierMixSection summary={summary} money={money} SectionHeading={SectionHeading} />

      <section>
        <SectionHeading title="What-if and cost per session" hint="Plan a model switch from real usage" />
        <WhatIfSimulator summary={summary} money={money} />
      </section>

      <section>
        <SectionHeading title="Where spend goes" hint="Highest-cost projects and sessions in the range" />
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <TableCard title="Top projects" info={SPEND_METRIC_INFO.topProjects} empty={summary.topProjects.length === 0} emptyText="No project information in this range.">
            <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
              <tr>
                <th className="pb-2.5 pr-3">Project</th>
                <th className="pb-2.5 pr-3">Tool</th>
                <th className="pb-2.5 pr-3 text-right">Requests</th>
                <th className="pb-2.5 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {summary.topProjects.map((p) => (
                <tr key={`${p.tool}:${p.projectId}`} className="border-b border-border last:border-0">
                  <td className="max-w-[240px] truncate py-2.5 pr-3 text-text-default" title={p.projectId}>{p.projectId}</td>
                  <td className="py-2.5 pr-3 text-text-muted">{TOOL_LABELS[p.tool]}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{p.requests.toLocaleString()}</td>
                  <td className="py-2.5 text-right font-medium tabular-nums text-text-default">{money(p.cost)}</td>
                </tr>
              ))}
            </tbody>
          </TableCard>
          <TableCard title="Top sessions" info={SPEND_METRIC_INFO.topSessions} empty={summary.topSessions.length === 0} emptyText="No session information in this range.">
            <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
              <tr>
                <th className="pb-2.5 pr-3">Session</th>
                <th className="pb-2.5 pr-3">Tool</th>
                <th className="pb-2.5 pr-3">Project</th>
                <th className="pb-2.5 pr-3 text-right">Requests</th>
                <th className="pb-2.5 pr-3 text-right">Duration</th>
                <th className="pb-2.5 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {summary.topSessions.map((s) => (
                <tr key={`${s.tool}:${s.sessionId}`} className="border-b border-border last:border-0">
                  <td className="py-2.5 pr-3 font-mono text-xs text-text-muted" title={s.sessionId}>{s.sessionId.slice(0, 8)}</td>
                  <td className="py-2.5 pr-3 text-text-muted">{TOOL_LABELS[s.tool]}</td>
                  <td className="max-w-[160px] truncate py-2.5 pr-3 text-text-muted" title={s.projectId ?? undefined}>{s.projectId ?? '—'}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{s.requests.toLocaleString()}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-text-muted">{formatDuration(s)}</td>
                  <td className="py-2.5 text-right font-medium tabular-nums text-text-default">{money(s.cost)}</td>
                </tr>
              ))}
            </tbody>
          </TableCard>
        </div>
      </section>

      <PricingBasisNote basis={summary.pricing} totalCost={summary.totalCost} unpricedModels={summary.unpricedModels}>
        GitHub Copilot is not included.
      </PricingBasisNote>
    </>
  );
}

function ToolSpendCard({ point, money }: { point: SpendToolPoint; money: (value: number) => string }) {
  if (point.cost === null) {
    return <KpiCard label={TOOL_LABELS[point.tool]} value="Not included" meta="No billing feed yet" info={SPEND_METRIC_INFO.copilot} accentClassName="bg-border" />;
  }
  return (
    <KpiCard
      label={TOOL_LABELS[point.tool]}
      value={money(point.cost)}
      meta={changeText(point.cost, point.previousCost, money) ?? undefined}
      accentClassName="bg-sky-blue"
    />
  );
}

function changeText(current: number, previous: number | null, money: (value: number) => string): string | null {
  if (previous === null) return null;
  if (previous === 0) return current === 0 ? 'No spend in either period' : `Up from ${money(0)} in the previous period`;
  const change = (current - previous) / previous;
  const direction = change > 0 ? 'Up' : change < 0 ? 'Down' : 'No change';
  return change === 0
    ? `${direction} from the previous period (${money(previous)})`
    : `${direction} ${formatPercent(Math.abs(change))} from ${money(previous)} in the previous period`;
}

function formatPercent(share: number): string {
  return `${(share * 100).toFixed(share < 0.1 ? 1 : 0)}%`;
}

function formatDuration(session: SpendSessionPoint): string {
  const minutes = Math.round((Date.parse(session.lastSeen) - Date.parse(session.firstSeen)) / 60_000);
  if (!Number.isFinite(minutes)) return '—';
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

/** Every UTC day in the range, so days without usage show as zero rather than disappearing. */
function daysInRange(from: string | undefined, to: string | undefined): string[] {
  const start = Date.parse(from ?? '');
  const end = Date.parse(to ?? '');
  if (!Number.isFinite(start) || !Number.isFinite(end)) return [];
  const days: string[] = [];
  for (let t = Math.floor(start / DAY_MS) * DAY_MS; t <= end; t += DAY_MS) days.push(new Date(t).toISOString().slice(0, 10));
  return days;
}

function DailySpendChart({ summary, money }: { summary: SpendSummary; money: (value: number) => string }) {
  const [mode, setMode] = useState<DailyMode>('tool');
  const pricedTools = summary.byTool.filter((t) => t.cost !== null).map((t) => t.tool);
  const topModels = [...new Map(summary.byModel.map((m) => [m.model, m])).keys()].slice(0, TOP_MODELS);
  const hasOther = summary.byModel.some((m) => !topModels.includes(m.model));
  const series =
    mode === 'tool'
      ? pricedTools.map((tool) => ({ key: tool as string, label: TOOL_LABELS[tool], color: TOOL_COLORS[tool] }))
      : [
          ...topModels.map((model, i) => ({ key: model, label: model, color: CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length] })),
          ...(hasOther ? [{ key: OTHER, label: OTHER, color: CHART_OTHER_COLOR }] : []),
        ];

  const byDate = new Map<string, Record<string, number | string>>(
    daysInRange(summary.range.from, summary.range.to).map((date) => [date, { date, ...Object.fromEntries(series.map((s) => [s.key, 0])) }]),
  );
  for (const point of summary.daily) {
    const row = byDate.get(point.date) ?? { date: point.date };
    const key = mode === 'tool' ? point.tool : topModels.includes(point.model) ? point.model : OTHER;
    row[key] = Number(row[key] ?? 0) + point.cost;
    byDate.set(point.date, row);
  }
  const data = [...byDate.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)));

  return (
    <ChartCard
      title={mode === 'tool' ? 'Daily spend by tool' : `Daily spend by model (top ${TOP_MODELS})`}
      caption="Indicative cost per UTC day"
      info={SPEND_METRIC_INFO.dailySpend}
      height="h-[26rem]"
      actions={<ModeToggle value={mode} onChange={setMode} />}
    >
      {summary.daily.length === 0 ? (
        <p className="flex h-full items-center justify-center text-sm text-text-muted">No Claude Code or Codex usage in this range.</p>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
            <XAxis dataKey="date" {...chartXAxisTickProps} />
            <YAxis {...chartAxisTickProps} tickFormatter={(value: number) => money(value)} width={80} />
            <Tooltip {...chartTooltipProps} formatter={((value: number) => money(value)) as never} />
            <Legend {...chartLegendProps} />
            {series.map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.label} stackId="spend" fill={s.color} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

function ModeToggle({ value, onChange }: { value: DailyMode; onChange: (value: DailyMode) => void }) {
  const options: { key: DailyMode; label: string }[] = [
    { key: 'tool', label: 'By tool' },
    { key: 'model', label: 'By model' },
  ];
  return (
    <div role="group" aria-label="Group daily spend" className="mr-2 mt-2 inline-flex rounded-pill border border-border bg-surface p-0.5">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          aria-pressed={value === option.key}
          onClick={() => onChange(option.key)}
          className={`min-h-[36px] rounded-pill px-4 text-xs font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue ${
            value === option.key ? 'bg-primary-blue text-text-inverse' : 'text-text-muted hover:bg-surface-alt hover:text-text-default'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function TokenTypeTotal({ label, color, value, total, money }: { label: string; color: string; value: number; total: number; money: (value: number) => string }) {
  return (
    <div className="rounded-card border border-border bg-surface p-4 shadow-sm">
      <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-text-muted">
        <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
        {label}
      </p>
      <p className="mt-1 text-xl font-bold tabular-nums text-text-default">{money(value)}</p>
      <p className="text-xs text-text-muted">{total > 0 ? `${formatPercent(value / total)} of spend` : '—'}</p>
    </div>
  );
}

function TokenTypeChart({ byModel, money }: { byModel: SpendModelPoint[]; money: (value: number) => string }) {
  const data = byModel.slice(0, 8).map((m) => ({ label: m.model, ...m.byType }));
  return (
    <ChartCard title="Token-type mix by model" caption="Top 8 models by indicative cost" info={SPEND_METRIC_INFO.tokenType} height="h-[26rem]">
      {data.length === 0 ? (
        <p className="flex h-full items-center justify-center text-sm text-text-muted">No token usage in this range.</p>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
            <XAxis type="number" {...chartAxisTickProps} tickFormatter={(value: number) => money(value)} />
            <YAxis type="category" dataKey="label" width={150} {...chartAxisTickProps} />
            <Tooltip {...chartTooltipProps} formatter={((value: number) => money(value)) as never} />
            <Legend {...chartLegendProps} />
            {TOKEN_TYPES.map((type) => (
              <Bar key={type.key} dataKey={type.key} name={type.label} stackId="type" fill={type.color} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

function TokenTypeTable({ byModel, money }: { byModel: SpendModelPoint[]; money: (value: number) => string }) {
  return (
    <TableCard title="Cost by token type, per model" info={SPEND_METRIC_INFO.tokenType} empty={byModel.length === 0} emptyText="No token usage in this range.">
      <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
        <tr>
          <th className="pb-2.5 pr-3">Model</th>
          <th className="pb-2.5 pr-3">Tool</th>
          <th className="pb-2.5 pr-3 text-right">Input</th>
          <th className="pb-2.5 pr-3 text-right">Output</th>
          <th className="pb-2.5 pr-3 text-right">Cache read</th>
          <th className="pb-2.5 pr-3 text-right">Cache write</th>
          <th className="pb-2.5 text-right">Total</th>
        </tr>
      </thead>
      <tbody>
        {byModel.map((m) => (
          <tr key={`${m.tool}:${m.model}`} className="border-b border-border last:border-0">
            <td className="py-2.5 pr-3 text-text-default">
              {m.model}
              {m.pricedAs === null && ' '}
              {m.pricedAs === null && (
                <span className="ml-1.5 inline-flex items-center gap-1 rounded-input bg-warn-bg px-1.5 py-0.5 text-[11px] text-text-default">
                  <i aria-hidden="true" className="fa-solid fa-triangle-exclamation text-warn-ink" />
                  Fallback rate
                </span>
              )}
            </td>
            <td className="py-2.5 pr-3 text-text-muted">{TOOL_LABELS[m.tool as Tool]}</td>
            <td className="py-2.5 pr-3 text-right tabular-nums">{money(m.byType.input)}</td>
            <td className="py-2.5 pr-3 text-right tabular-nums">{money(m.byType.output)}</td>
            <td className="py-2.5 pr-3 text-right tabular-nums">{money(m.byType.cacheRead)}</td>
            <td className="py-2.5 pr-3 text-right tabular-nums" title={m.tool === 'codex' ? 'Codex has no cache-write charge' : undefined}>
              {m.tool === 'codex' ? '—' : money(m.byType.cacheWrite)}
            </td>
            <td className="py-2.5 text-right font-medium tabular-nums text-text-default">{money(m.cost)}</td>
          </tr>
        ))}
      </tbody>
    </TableCard>
  );
}

